/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import ConnectReferenceResolvers from 'reference-resolvers/ConnectReferenceResolvers';
import FilterOperatorRefinableInput from 'customer-data-filters/components/operator/FilterOperatorRefinableInput';
import { fromObjectSegFilterStrict } from 'customer-data-filters/converters/objectSeg/fromObjectSegFilter';
import { toObjectSegFilterStrict } from 'customer-data-filters/converters/objectSeg/toObjectSegFilter';
import FilterOperatorErrorRecord from 'customer-data-filters/filterQueryFormat/FilterOperatorErrorRecord';
import Any from 'customer-data-filters/filterQueryFormat/operator/Any';
import I18n from 'I18n';
import { Small } from 'foundations-components/transitional/components/Small';
import { fromJS, List } from 'immutable';
import { useMemo, useState, useEffect } from 'react';
import debounce from 'react-utils/debounce';
import { Button } from 'foundations-components/transitional/components/Button';
import { H7 } from 'foundations-components/transitional/components/H7';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { getReferenceType } from '../../../../metadata/references/external/external-mapping';
import { getPropertyResolver } from 'reference-resolvers-lite/utils/getPropertyResolver';
import { htmlEscapedFilter, htmlNormalizedToTextFilter, isHtmlFieldType } from '../../filter-utils';
import { getInputComponent, getLabelString, getObjectTypeFamily, getOperatorLabel, getOperators, getPlaceholder, buildSpecialOptionsByReferenceType, toDSFieldRecord, toObjectSegAny, getPropertyFieldDefinitions, canUseReferenceResolverLite, canUseReferenceResolverLiteForFilterInput, SNOWFLAKE_FILTER_FAMILY } from '../../xo-filter-utils';
import { useProperty, useTableSource } from '../SourceMeta';
import { hasObjectTypeId } from '../../../../utils/source-utils';
import { validateFilterBetweenValue, validateFilterOperatorValue } from '../../filter-validation-utils';
import { CloneFilterSelect } from '../shared/CloneFilterSelect';
import { GroupFilterControl } from '../shared/GroupFilterControl';
import { getIsUngated, useFilterRelevantUserInfo } from '../../../../../shared/lib/utils';
import { Metrics } from '../../../../../shared/Metrics';
import { REPORTING_LEGACY_OPERATOR_USAGE } from 'reporting-data/constants/trackingMetrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NO_ERROR = FilterOperatorErrorRecord({
  error: false
});
const PropertyFilterPanel = ({
  filter,
  groupId,
  resolvers,
  onDone,
  onCancel,
  onCloneFilter
}) => {
  const {
    field: {
      table: tableName,
      name: fieldName
    }
  } = filter;
  const source = useTableSource(tableName);
  const property = useProperty(tableName, fieldName);
  const [nullIssueRelatedState, setNullIssueRelatedState] = useState({
    conditionBranchPath: List(),
    showIncludeObjectsWithNoValueSetByOperator: {},
    showIncludeObjectsWithNoAssociatedObjectsByFilterBranch: {},
    defaultNullValueByOperator: {}
  });
  useEffect(() => {
    Metrics.counter(REPORTING_LEGACY_OPERATOR_USAGE, {
      propertyType: 'property-filter'
    }).increment();
  }, []);
  if (property == null) {
    throw new Error('PropertyFilterPanel can only be rendered when there is a matching existing property in report meta!');
  }
  if (source == null) {
    throw new Error('PropertyFilterPanel can only be rendered when there is a table description in the report!');
  }
  const [draftFilter, setDraft] = useState(filter);
  const [draftGroupId, setGroup] = useState(groupId);
  const {
    userId,
    userGates
  } = useFilterRelevantUserInfo();
  const mappedProps = useMemo(() => {
    let {
      filter: propertyFilter
    } = draftFilter;
    if (isHtmlFieldType(property)) {
      propertyFilter = htmlNormalizedToTextFilter(draftFilter).filter;
    }
    const field = toDSFieldRecord(property, source, userGates);
    const filterFamily = hasObjectTypeId(source) ? getObjectTypeFamily(source.objectTypeId) : source.dataSourceId;
    const operator = propertyFilter && propertyFilter.get('operation') ? fromObjectSegFilterStrict(propertyFilter.toJS(), () => field, SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true) : Any.of(field);
    const getReferencedObjectType = () => getReferenceType(source, property);
    const getFamilyValueResolver = () => {
      const useReferenceResolverLite = canUseReferenceResolverLite(source, property, userGates) && canUseReferenceResolverLiteForFilterInput(property);
      const referenceResolverLite = useReferenceResolverLite ? getPropertyResolver({
        property: field,
        objectTypeId: source.objectTypeId
      }) : null;
      return referenceResolverLite || resolvers[getReferencedObjectType()] || null;
    };
    return {
      currencyCode: 'USD',
      error: NO_ERROR,
      showIncludeUnknownValues: true,
      isRollingDateOffsetInputEnabled: false,
      isXoEnabled: true,
      isFiscalYearEnabled: true,
      filterFamily,
      getIsUngated: gate => getIsUngated(gate, userGates),
      getDefaultTimezone: () => I18n.moment.portalTz().tz(),
      getFilterFamilyObjectName: () => '',
      getObjectPropertyLabel: () => '',
      getOperatorLabel,
      getReferencedObjectType,
      getSpecialOptionsForReferenceType: buildSpecialOptionsByReferenceType(property.type, userId),
      getFamilyValueResolver,
      getInputComponent: () => getInputComponent(source, property, operator),
      getLabelString: () => getLabelString(field),
      getPlaceholder: () => getPlaceholder(operator),
      getOperators: () => getOperators(source, property, userGates),
      value: operator,
      fieldDefinitions: getPropertyFieldDefinitions(),
      nullIssueRelatedState,
      updateNullIssueRelatedState: nullState => setNullIssueRelatedState(nullState)
    };
  }, [draftFilter, source, property, resolvers, userId, nullIssueRelatedState, userGates]);
  const handleChange = ({
    target: {
      value: operator
    }
  }) => {
    const nextObjectSegFilter = operator instanceof Any ? toObjectSegAny(operator) : toObjectSegFilterStrict(operator, SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true);
    const nextFilter = filter.set('filter', fromJS(nextObjectSegFilter));
    if (isHtmlFieldType(property)) {
      const updatedFilter = htmlEscapedFilter(nextFilter);
      setDraft(updatedFilter);
    } else {
      setDraft(nextFilter);
    }
  };
  const debouncedHandleChange = debounce(handleChange, 300);
  const handleDone = () => {
    onDone({
      draftFilter,
      draftGroupId
    });
  };
  const handleCloneFilter = groupIndex => {
    onCloneFilter({
      draftFilter,
      groupId: groupIndex
    });
  };
  const isValidFilter = validateFilterOperatorValue(draftFilter, userGates);
  const isValidBetweenValue = validateFilterBetweenValue(draftFilter);
  const isSubmitDisabled = !isValidFilter || !isValidBetweenValue;
  const accountTimezone = I18n.moment.portalTz().format('z');
  const timezoneText = I18n.text('reporting-snowflake.filter-editor.account-timezone', {
    accountTimezone
  });
  const disabledButtonTooltipCopy = I18n.text('reporting-snowflake.filter-editor.editor-panel.incomplete-filter-options');
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "filter-edit-panel",
    children: [/*#__PURE__*/_jsx(H7, {
      marginBlockEnd: "0",
      children: property.label || property.name
    }), /*#__PURE__*/_jsx(Small, {
      children: timezoneText
    }), /*#__PURE__*/_jsx(FilterOperatorRefinableInput, Object.assign({}, mappedProps, {
      onChange: debouncedHandleChange
    })), /*#__PURE__*/_jsx(GroupFilterControl, {
      currentGroupId: draftGroupId,
      onGroupSelected: nextGroupId => setGroup(nextGroupId)
    }), /*#__PURE__*/_jsxs(TFlex, {
      justify: "between",
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Tooltip, {
          title: disabledButtonTooltipCopy,
          placement: "top right",
          disabled: !isSubmitDisabled,
          children: /*#__PURE__*/_jsx(Button, {
            use: "tertiary",
            size: "xs",
            "data-test-id": "filter-editor-panel-apply-btn",
            disabled: isSubmitDisabled,
            onClick: handleDone,
            children: I18n.text('reporting-snowflake.filter-editor.editor-panel.apply')
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          size: "xs",
          onClick: onCancel,
          children: I18n.text('reporting-snowflake.filter-editor.editor-panel.cancel')
        })]
      }), /*#__PURE__*/_jsx(CloneFilterSelect, {
        currentGroupId: groupId,
        disabled: isSubmitDisabled,
        tooltipMessage: disabledButtonTooltipCopy,
        onCloneFilter: handleCloneFilter
      })]
    })]
  });
};
export default ConnectReferenceResolvers(resolvers => ({
  resolvers
}))(PropertyFilterPanel);